import { useEffect, useRef, useState, type ReactNode } from 'react';
import { getMyJournal } from '../lib/api';
import { getLang, localeOf, useT } from '../lib/i18n';
import { haptic } from '../lib/telegram';
import { useScrollFocus } from '../lib/scroll-focus';
import { COACH_TZ, fmtDayNum, fmtWeekday } from '../lib/time';
import { shortType } from './SessionRow';
import { Vis } from './Vis';

/**
 * Every past lesson, newest first, one short entry each that opens on
 * tap, with the goals set along the way in the same timeline.
 *
 * The coach and the player read the same journal. The player's copy
 * never contains the private note: the server does not send it. The
 * coach's copy marks every note with who reads it.
 */

export interface JournalSession {
  booking_id: string;
  status: string;
  focus: string[] | null;
  drills: string | null;
  shared_note: string | null;
  /** A pair or group session's note for everyone on it. */
  common_note?: string | null;
  /** A task for this player only, next to the shared homework. */
  homework_personal?: string | null;
  /** Their ratings for this session; shown for pairs and groups. */
  scores?: { name: string; nameRu: string | null; score: number }[] | null;
  homework: string | null;
  client_note: string | null;
  coach_note: string | null;
  starts_at: string;
  ends_at: string;
  kind: string;
  title: string;
  people: number;
}
export interface JournalGoal { text: string; since: string; until: string | null; sessions: number }
export interface JournalData {
  current: { text: string; since: string | null } | null;
  goals: JournalGoal[];
  sessions: JournalSession[];
  coachName?: string | null;
}

const fmt = (o: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(localeOf(getLang()), { timeZone: COACH_TZ, ...o });
const dayMonth = (iso: string) => fmt({ day: 'numeric', month: 'short' }).format(new Date(iso));
/** "September 2026" / "сентябрь 2026", without Russian's trailing "г.". */
const monthKey = (iso: string) => {
  const d = new Date(iso);
  return `${fmt({ month: 'long' }).format(d)} ${fmt({ year: 'numeric' }).format(d)}`;
};
/** "8–22 Sept", "26 Aug – 8 Sept", or one day when it began and ended on it. */
const dateSpan = (from: string, to: string) =>
  fmt({ day: 'numeric', month: 'short' }).formatRange(new Date(from), new Date(to));
const lines = (text: string | null) => (text ?? '').split('\n').map((l) => l.trim()).filter(Boolean);

type Item =
  | { at: string; session: JournalSession }
  | { at: string; goal: JournalGoal };

export function Journal({ data, mode, clientName, onEdit, hideGoal }: {
  data: JournalData;
  mode: 'coach' | 'player';
  clientName?: string;
  onEdit?: (bookingId: string) => void;
  /** The player's My profile shows the goal at the top instead (MyGoal). */
  hideGoal?: boolean;
}) {
  const t = useT();
  const coach = mode === 'coach';
  const [open, setOpen] = useState<string | null>(data.sessions[0]?.booking_id ?? null);

  // Goals come newest first; each one's "before" is the next in the list.
  const items: Item[] = [
    ...data.sessions.map((s) => ({ at: s.starts_at, session: s })),
    ...data.goals.map((g) => ({ at: g.since, goal: g })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  let lastMonth = '';
  return (
    <div className="jr">
      {!hideGoal && <GoalCard data={data} coach={coach} />}

      <div className="jr-head">
        <b>{t('jr.count', { n: data.sessions.length, lessons: t.word('jr.lessons', data.sessions.length) })}</b>
      </div>

      {data.sessions.length === 0 && <div className="card__when jr-empty">{t('jr.empty')}</div>}

      {items.map((it) => {
        const month = monthKey(it.at);
        const header = month !== lastMonth ? <div className="jr-month">{month}</div> : null;
        lastMonth = month;

        if ('goal' in it) {
          return (
            <div key={`g${it.at}`}>
              {header}
              <div className="jr-marker">
                <b>{coach ? t('jr.newGoal') : t('jr.newGoalFrom', { coach: data.coachName ?? '' })}</b> {it.goal.text}
              </div>
            </div>
          );
        }

        const s = it.session;
        const missed = s.status === 'no_show';
        const drills = lines(s.drills);
        const minutes = Math.round((new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime()) / 60000);
        const type = `${s.people > 1 ? t('co.pp.groupOf', { n: s.people }) : shortType(s.title)} · ${t('jr.min', { n: minutes })}`;
        // What it was about, or failing that the first thing written, so
        // an entry never just repeats the lesson type above it.
        const focus = (s.focus ?? []).join(' · ');
        const written = drills[0] ?? s.common_note ?? s.shared_note ?? '';
        const headline = missed ? (coach ? t('co.today.noShow') : t('jr.missed'))
          : focus || written || t('jr.noNotes');
        const preview = focus ? written : '';
        const bare = !missed && !focus && !written;
        const hasBody = !missed || Boolean(s.coach_note);
        const isOpen = open === s.booking_id && hasBody;
        const d = new Date(s.starts_at);

        return (
          <div key={s.booking_id}>
            {header}
            <div className={`jr-entry${isOpen ? ' jr-entry--open' : ''}${missed ? ' jr-entry--missed' : ''}`}>
              <button className="jr-entry__row" disabled={!hasBody} aria-expanded={isOpen}
                onClick={() => { haptic.tap(); setOpen(isOpen ? null : s.booking_id); }}>
                <span className="jr-date"><b>{fmtDayNum(d)}</b><span>{fmtWeekday(d)}</span></span>
                <span className="jr-what">
                  <span className="jr-what__t">{type}</span>
                  <span className={`jr-what__f${bare ? ' jr-what__f--bare' : ''}`}>{headline}</span>
                  {!isOpen && preview && !missed && <span className="jr-what__p">{preview}</span>}
                </span>
                {coach && (
                  <span className="jr-icons" aria-hidden="true">
                    {(s.shared_note || s.common_note || s.homework) && '👁'}{s.coach_note && '🔒'}
                  </span>
                )}
              </button>

              {isOpen && (
                <div className="jr-body">
                  {drills.length > 0 && (
                    <div>
                      <Field text={coach ? t('jr.drills') : t('jr.drillsPlayer')} who={coach ? 'player' : null} />
                      <ol>{drills.map((l, i) => <li key={i}>{l}</li>)}</ol>
                    </div>
                  )}
                  {s.common_note && (
                    <div>
                      <Field text={t('jr.common')} who={coach ? 'player' : null} />
                      <p>{s.common_note}</p>
                    </div>
                  )}
                  {s.shared_note && (
                    <div>
                      <Field text={coach ? t('jr.shared', { name: clientName ?? '' }) : t('jr.fromCoach', { coach: data.coachName ?? '' })}
                        who={coach ? 'player' : null} />
                      <p>{s.shared_note}</p>
                    </div>
                  )}
                  {(s.homework || s.homework_personal) && (
                    <div>
                      <Field text={t('jr.homework')} who={coach ? 'player' : null} />
                      {s.homework && <p>🎯 {s.homework}</p>}
                      {s.homework_personal && <p>🎯 <b>{t('jr.yourTask')}:</b> {s.homework_personal}</p>}
                    </div>
                  )}
                  {s.people > 1 && s.scores && s.scores.length > 0 && (
                    <div>
                      <Field text={t('jr.ratings')} who={null} />
                      <p className="jr-scores">{s.scores.map((x) => `${getLang() === 'ru' && x.nameRu ? x.nameRu : x.name} ${x.score}`).join(' · ')}</p>
                    </div>
                  )}
                  {coach && s.coach_note && (
                    <div>
                      <Field text={t('jr.private')} who="me" />
                      <p>{s.coach_note}</p>
                    </div>
                  )}
                  {s.client_note && (
                    <div>
                      <Field text={coach ? t('jr.asked', { name: clientName ?? '' }) : t('jr.askedPlayer')} who={null}
                        extra={coach ? <span className="vis vis--player">{t('jr.fromPlayer', { name: clientName ?? '' })}</span> : undefined} />
                      <p>{s.client_note}</p>
                    </div>
                  )}
                  {!drills.length && !s.shared_note && !s.common_note && !s.homework && !s.client_note && !(coach && s.coach_note) && (
                    <p className="card__when">{t('jr.nothing')}</p>
                  )}
                  {coach && onEdit && (
                    <button className="sess__link jr-edit" onClick={() => onEdit(s.booking_id)}>{t('co.pp.edit')}</button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Field({ text, who, extra }: { text: string; who: 'me' | 'player' | null; extra?: ReactNode }) {
  return (
    <div className="jr-field">
      <span>{text}</span>
      {who && <Vis who={who} />}
      {extra}
    </div>
  );
}


/** «Над чем работаем»: the current goal, since when, and the earlier ones. */
function GoalCard({ data, coach }: { data: JournalData; coach: boolean }) {
  const t = useT();
  const current = data.current;
  const currentGoal = data.goals.find((g) => g.until === null && g.text === current?.text);
  const earlier = data.goals.filter((g) => g !== currentGoal && g.until !== null);
  if (!current) return null;
  return (
    <div className="jr-goal">
      <div className="jr-goal__top">
        <span className="jr-eyebrow">{t('co.rate.workingOnLabel')}</span>
        {coach && <Vis who="player" />}
      </div>
      <div className="jr-goal__text">{current.text}</div>
      {currentGoal && (
        <div className="jr-goal__meta">
          {t('jr.since', {
            date: dayMonth(currentGoal.since), n: currentGoal.sessions,
            sessions: t.word('co.today.sessions', currentGoal.sessions),
          })}
        </div>
      )}
      {earlier.length > 0 && (
        <div className="jr-goal__hist">
          {earlier.map((g) => (
            <div key={g.since} className="jr-goal__old">
              <s>{g.text}</s>
              <small>
                {dateSpan(g.since, g.until!)} · {g.sessions} {t.word('co.today.sessions', g.sessions)}
              </small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The player's journal, fetched once for My profile: the goal on top
 * (MyGoal) and the journal further down (MyJournal) share one request.
 */
let journalOnce: Promise<JournalData> | null = null;
export const loadMyJournal = () => {
  journalOnce ??= getMyJournal().finally(() => { setTimeout(() => { journalOnce = null; }, 0); });
  return journalOnce;
};

/** My profile, at the top: «Над чем работаем», when the coach has set it. */
export function MyGoal() {
  const [data, setData] = useState<JournalData | null>(null);
  useEffect(() => { loadMyJournal().then(setData).catch(() => setData(null)); }, []);
  return data?.current ? <div className="jr jr--top"><GoalCard data={data} coach={false} /></div> : null;
}

/** The player's My profile tab: their own journal (the goal is shown at the top, by MyGoal). */
/** `focusAt`: set when the app was opened on the journal (?tab=journal); it scrolls into view. */
export function MyJournal({ focusAt }: { focusAt?: number } = {}) {
  const t = useT();
  const [data, setData] = useState<JournalData | null | false>(null);
  const here = useRef<HTMLDivElement>(null);
  useEffect(() => { loadMyJournal().then(setData).catch(() => setData(false)); }, []);
  useScrollFocus(here, data ? focusAt : undefined);
  if (data === null) return <div className="list"><div className="skeleton" /><div className="skeleton" /></div>;
  if (data === false) return <div className="empty">{t('jr.failed')}</div>;
  return <div ref={here}><Journal data={data} mode="player" hideGoal /></div>;
}
